import { ColDef, GridOptions, ValueFormatterParams } from 'ag-grid-community';

import { formatCurrency } from '@app/shared/utils/number.utils';
import {
  AgGridDropdownMenuCellRendererComponent,
  IAgGridDropdownMenuCellRendererParams,
} from 'v2/modules/shared/components/ag-grid-dropdown-menu-cell-renderer/ag-grid-dropdown-menu-cell-renderer.component';
import { AgGridValueFormatter } from 'v2/modules/shared/util/ag-grid-value-formatter.utils';
import { AgGridUtils } from 'v2/modules/shared/util/ag-grid.utils';

import {
  EShipmentsDetailGridField,
  EShipmentsGridField,
  IShipmentsGridColumnDefContext,
  IShipmentsGridOptionsContext,
} from '../types/shipments-grid.types';
import { ShipmentsGridCellRendererUtils } from '../utils/shipments-grid-cell-renderer.utils';

const getShipmentsGridColumnConfig = (
  context: IShipmentsGridColumnDefContext,
): ColDef[] => {
  return [
    {
      cellClass: 'lum-ag-expand-cell',
      cellRenderer: 'agGroupCellRenderer',
      filter: false,
      filterParams: null,
      floatingFilter: false,
      headerClass: 'lum-ag-expand-cell lum-ag-expand-cell--header',
      lockPinned: true,
      lockPosition: true,
      maxWidth: 40,
      minWidth: 40,
      pinned: 'left',
      resizable: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
      suppressNavigable: true,
      width: 40,
    },
    // Uncomment this once the checkbox selection is implemented
    // {
    //   headerCheckboxSelection: true,
    //   checkboxSelection: true,
    //   filter: false,
    //   filterParams: null,
    //   floatingFilter: false,
    //   lockPinned: true,
    //   lockPosition: true,
    //   resizable: false,
    //   sortable: false,
    //   suppressColumnsToolPanel: true,
    //   suppressMenu: true,
    //   suppressMovable: true,
    //   suppressNavigable: true,
    //   width: 40,
    //   maxWidth: 40,
    //   minWidth: 40,
    //   pinned: 'left',
    // },
    {
      headerName: 'Shipment ID',
      field: EShipmentsGridField.SHIPMENT_ID,
    },
    {
      headerName: 'Fulfillment ID',
      field: EShipmentsGridField.FULFILLMENT_ID,
    },
    {
      headerName: 'Invoice ID',
      sortable: false,
      filter: false,
      field: 'invoice.id',
    },
    {
      headerName: 'Shipment Status',
      field: EShipmentsGridField.STATUS,
      // for dropdown
      // filterParams: {
      //   filterOptions: [
      //     {
      //       displayKey: 'empty',
      //       displayName: 'All',
      //       predicate: () => true,
      //       numberOfInputs: 0,
      //     },
      //     {
      //       displayKey: 'Active',
      //       displayName: 'Active',
      //       predicate: (value) => value,
      //       numberOfInputs: 0,
      //     },
      //     {
      //       displayKey: 'not-active',
      //       displayName: 'Not Active',
      //       predicate: (value) => value,
      //       numberOfInputs: 0,
      //     },
      //   ],
      //   defaultOption: 'empty',
      //   maxNumConditions: 1,
      //   debounceMs: 1000,
      // },
      // for select dropdown
      // filter: 'agSetColumnFilter',
      // filterParams: {
      //   values: ['non-variance', 'variance'], // The raw values
      //   defaultToNothingSelected: true,
      //   suppressSelectAll: false,
      // },
    },
    {
      headerName: 'Shipment Date',
      field: EShipmentsGridField.SHIP_DATE,
      filter: 'agDateColumnFilter',
      valueFormatter: AgGridValueFormatter.DATE,
    },
    {
      headerName: 'Tracking Number',
      field: EShipmentsGridField.TRACKING_NUMBER,
    },
    {
      headerName: 'Carrier Code',
      field: EShipmentsGridField.CARRIER_CODE,
    },
    {
      headerName: 'Shipment Cost',
      field: EShipmentsGridField.SHIPMENT_COST,
      valueFormatter: ({ value }: ValueFormatterParams) => formatCurrency(value),
    },
    {
      headerName: 'Customer Name',
      field: EShipmentsGridField.CUSTOMER_NAME,
      sortable: false,
      filter: false,
      cellRenderer: ShipmentsGridCellRendererUtils.customerNameCellRenderer,
    },
    {
      headerName: 'Shipping Address',
      field: EShipmentsGridField.SHIPPING_ADDRESS,
      sortable: false,
      filter: false,
      cellRenderer: ShipmentsGridCellRendererUtils.shippingAddressCellRenderer,
    },
    {
      filter: false,
      floatingFilter: false,
      lockPinned: true,
      lockPosition: true,
      maxWidth: 40,
      minWidth: 40,
      pinned: 'right',
      resizable: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
      suppressNavigable: true,
      width: 40,
      cellRenderer: AgGridDropdownMenuCellRendererComponent,
      cellRendererParams: {
        options: context.dropdownOptions,
      } as IAgGridDropdownMenuCellRendererParams,
    },
  ] as ColDef[];
};

const getExpandableRowColumnDefs = (
  context: IShipmentsGridOptionsContext, // eslint-disable-line @typescript-eslint/no-unused-vars
): ColDef[] => {
  return [
    { field: EShipmentsDetailGridField.SKU, headerName: 'SKU' },
    { field: EShipmentsDetailGridField.NAME, headerName: 'Name' },
    {
      field: EShipmentsDetailGridField.QUANTITY,
      headerName: 'Quantity Shipped',
      valueFormatter: AgGridValueFormatter.DECIMAL_PLACES,
    },
    {
      field: EShipmentsDetailGridField.UNIT_PRICE,
      headerName: 'Cost',
      valueFormatter: ({ value }: ValueFormatterParams) => formatCurrency(value),
    },
    { field: EShipmentsDetailGridField.STATUS, headerName: 'Status' },
  ];
};

const getShipmentsGridOptions = (
  context: IShipmentsGridOptionsContext,
): GridOptions => {
  return {
    rowSelection: 'multiple',
    rowModelType: 'serverSide',
    pagination: true,
    paginationPageSize: 100,
    suppressScrollOnNewData: true,
    defaultColDef: {
      sortable: true,
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: (params) => {
        const filterOptions = AgGridUtils.getFilterOptions(
          params.column.getColDef().filter,
        );

        return {
          filterOptions: filterOptions.options,
          defaultOption: filterOptions.default,
          maxNumConditions: 1,
          debounceMs: 1000,
        };
      },
      resizable: true,
      minWidth: 100,
    },
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressRowGroups: true,
          },
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
      ],
      defaultToolPanel: '',
    },
    masterDetail: true,
    detailCellRendererParams: {
      detailGridOptions: {
        columnDefs: getExpandableRowColumnDefs(context),
        defaultColDef: {
          flex: 1,
          resizable: false,
          sortable: false,
          suppressMenu: false,
        },
      },
      getDetailRowData: (params) => {
        if (!params.data.shipmentItems || params.data.shipmentItems.length === 0) {
          return context.fetchShipmentDetails(params.data.id);
        } else {
          params.successCallback(params.data.shipmentItems);
        }
      },
    },
    onColumnMoved: context.saveGridState,
    onColumnResized: context.saveGridState,
    onFilterChanged: context.saveGridState,
    onSortChanged: context.saveGridState,
    onSelectionChanged: context.updateSelectedRows,
  };
};

export const ShipmentsGridConfig = {
  getShipmentsGridColumnConfig,
  getShipmentsGridOptions,
};
